import { faAngleDown } from "@fortawesome/free-solid-svg-icons/faAngleDown";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import { Select, Tooltip } from "../utils/local-antd";
import type { SelectProps } from "../utils/local-antd";

import React, { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Spacing } from "../theme/Spacing";
import { AtiraIcon } from "./AtiraIcon";
import { Flex } from "./Flex";
import { Text } from "./Text";
import { useTheme } from "../ThemeContext";
import { ThemeType } from "../theme/Theme";

const StyledSelectContainer = styled(Flex)<{
  height?: string;
  valid?: boolean;
}>`
  flex-direction: column;
  gap: ${Spacing.s};

  .ant-select {
    height: ${({ height }) => height || "2.3rem"} !important;
  }

  .ant-select-selector:hover {
    border: 1px solid ${({ theme }) => theme.main} !important;
  }

  .ant-select-dropdown {
    z-index: 10000 !important;
  }

  input {
    height: 100% !important;
  }

  ${({ theme, valid }) =>
    !valid
      ? `
    .ant-select {
      border: 1px solid ${theme.danger} !important;
    }
  `
      : ""}
`;

const Required = styled.span`
  color: ${({ theme }) => theme.danger};
`;

const InfoSpan = styled.span`
  padding-inline-end: calc(${Spacing.s} / 2);
  cursor: help;
`;

interface AtiraSelectProps extends SelectProps {
  width?: string;
  height?: string;
  borderRadius?: string;
  title?: string;
  titleIcon?: React.ReactNode;
  titleColor?: keyof ThemeType;
  containerStyle?: CSSProperties;
  onChange: (v: string) => void;
  required?: boolean;
  valid?: boolean;
  errorMessage?: string;
  info?: string | React.ReactNode;
}

export const AtiraSelect: React.FC<AtiraSelectProps> = ({
  onChange,
  options,
  value,
  width,
  height,
  title,
  titleIcon,
  titleColor,
  borderRadius,
  containerStyle,
  required = false,
  valid = true,
  errorMessage,
  info,
  ...props
}) => {
  const { t } = useTranslation();

  return (
    <StyledSelectContainer
      className="atira-select"
      style={containerStyle}
      height={height}
      valid={valid}
    >
      {title ? (
        <Flex alignItems="center" gap="xs">
          {info ? (
            <Tooltip title={info}>
              <InfoSpan>
                <AtiraIcon icon={faInfoCircle} color="main" />
              </InfoSpan>
            </Tooltip>
          ) : null}
          <Text color={titleColor}>
            {title}
            {required ? <Required> *</Required> : null}
          </Text>
        </Flex>
      ) : null}
      <Flex alignItems="center" width={"100%"} height={"100%"}>
        {info && !title ? (
          <Tooltip title={info}>
            <InfoSpan>
              <AtiraIcon icon={faInfoCircle} />
            </InfoSpan>
          </Tooltip>
        ) : null}

        {titleIcon ? <Flex marginInlineEnd="xs">{titleIcon}</Flex> : null}
        <Select
          onChange={onChange}
          value={value}
          options={options}
          suffixIcon={<AtiraIcon icon={faAngleDown} color="darkSub" />}
          placeholder={props.placeholder || t("common.select-placeholder")}
          style={{ width: "100%" }}
          {...props}
        />
      </Flex>

      {valid === false && errorMessage ? (
        <Text color="danger" fontSize="s">
          {errorMessage}
        </Text>
      ) : null}
    </StyledSelectContainer>
  );
};
